import * as React from 'react';
import { IPet } from '@/shared/interfaces';
import { PetTypeEnum } from '@/shared/enums';
import { R } from '@/features/profile/resources/ProfileResources.resx';
import {
    Button,
    ConfirmationModal,
    HorizontalContainer,
    IconButton,
    toast,
    VerticalContainer,
} from '@/components';
import { useState } from 'react';
import { PetModal } from '@/features/profile/widgets/pets/ui/PetModal';
import { useHydrateAtoms } from 'jotai/utils';
import { petsAtom } from '@/features/profile/widgets/pets/model/petAtoms';
import { useAtom } from 'jotai/index';
import { useMutation, useQuery } from '@tanstack/react-query';
import { deletePet } from './api/deletePet';
import { fetchPets } from '@/api/pets/fetchPets';
import {
    CatIcon,
    DogIcon,
    EditIcon,
    RabbitIcon,
    RodentIcon,
    TrashIcon,
} from '@/components/Icons';

export type PetsProps = {
    bootstrappedPets: IPet[];
};

const getPetIcon: (petType: PetTypeEnum) => React.ReactNode = (petType) => {
    switch (petType) {
        case PetTypeEnum.Dog:
            return <DogIcon className="icon-box" />;
        case PetTypeEnum.Cat:
            return <CatIcon className="icon-box" />;
        case PetTypeEnum.Rabbit:
            return <RabbitIcon className="icon-box" />;
        case PetTypeEnum.Rodent:
            return <RodentIcon className="icon-box" />;
        default:
            return <DogIcon className="icon-box" />;
    }
};

export const Pets: ({ bootstrappedPets }: PetsProps) => React.ReactNode = ({
    bootstrappedPets,
}) => {
    useHydrateAtoms([[petsAtom, bootstrappedPets]]);
    const [pets, setPets] = useAtom(petsAtom);
    const [selectedPet, setSelectedPet] = useState<IPet>();
    const [isPetModalOpen, setIsPetModalOpen] = useState(false);
    const [petToDelete, setPetToDelete] = useState<IPet | null>(null);

    const openPetModal = (pet: IPet | null): void => {
        setSelectedPet(pet);
        setIsPetModalOpen(true);
    };

    const { refetch } = useQuery({
        queryKey: ['pets'],
        queryFn: fetchPets,
        enabled: false,
    });

    const handleRefetch = async () => {
        try {
            const { data: pets } = await refetch();
            setPets(pets);
        } catch (e) {
            window.location.reload();
        }
    };

    const deletePetMutation = useMutation({
        mutationFn: deletePet,
        onSuccess: handleRefetch,
        onSettled: () => setPetToDelete(null),
        onError: () => {
            toast.addToast({
                type: 'error',
                message: R.ErrorDeletePet,
            });
        },
    });

    return (
        <>
            <VerticalContainer
                aria-describedby="pets-profile-description"
                spacing="md"
            >
                <h2 id="heading-Pets">{R.Pets}</h2>
                <div
                    id="pets-profile-description"
                    className="mb-2 max-w-prose"
                    dangerouslySetInnerHTML={{
                        __html: R.Profile_PetsDescription,
                    }}
                />

                {pets.map((pet) => (
                    <HorizontalContainer className="items-center" key={pet.Id}>
                        <button
                            className="border-border-control-default flex w-full max-w-sm cursor-pointer items-center justify-between gap-4 rounded-2xl border p-4 text-left hover:bg-(--color-bg-subtle)"
                            tabIndex={0}
                            aria-labelledby="pet-name edit-action"
                            onClick={() => openPetModal(pet)}
                        >
                            {getPetIcon(pet.Type)}
                            <div className="flex-1">
                                <div className="text-text-primary text-lg leading-tight font-bold">
                                    {pet.Name}
                                </div>
                                <div className="text-text-secondary text-sm leading-tight font-normal">
                                    {pet.Race}
                                </div>
                            </div>
                            <EditIcon
                                id="edit-action"
                                className="flex-none"
                                width="24"
                                height="24"
                            />
                        </button>

                        <IconButton
                            icon={<TrashIcon variant="error" />}
                            onClick={() => setPetToDelete(pet)}
                            tooltip={R.RemovePet}
                            aria-labelledby="pet-name delete-action"
                        />
                    </HorizontalContainer>
                ))}

                <Button variant="primary" onClick={() => openPetModal(null)}>
                    {R.AddPet}
                </Button>
            </VerticalContainer>

            {isPetModalOpen && (
                <PetModal
                    pet={selectedPet}
                    onClose={(refetch) => {
                        if (refetch) {
                            handleRefetch();
                        }
                        setIsPetModalOpen(false);
                    }}
                />
            )}

            {petToDelete && (
                <ConfirmationModal
                    title={R.RemovePet}
                    onClose={() => setPetToDelete(null)}
                    message={R.RemovePetText}
                    confirmButtonText={R.RemovePet}
                    confirmButtonClick={() =>
                        deletePetMutation.mutate(petToDelete)
                    }
                    cancelButtonText={R.KeepPet}
                    confirmIsProcessing={deletePetMutation.isPending}
                />
            )}
        </>
    );
};
